import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Save, Loader2, Search, Plus, Pencil, Trash2, User as UserIcon, MapPin, Package, Users as UsersIcon, Tag, X, Check, Ticket } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { StatusToggle } from '@/components/ui/StatusToggle'
import { Sheet } from '@/components/ui/Sheet'
import { FormSidemenu, type FormSection } from '@/components/ui/FormSidemenu'
import { toast } from '@/components/ui/Toast'
import { maskPhoneInput, onlyDigits, brl } from '@/lib/format'
import { CollaboratorsService, type CollaboratorDependent, type ReleasedPlan, type PlanEligibility } from '@/services/modules/CollaboratorsService'
import { MyCompanyService, type SimpleOption, type DepartmentOption } from '@/services/modules/MyCompanyService'
import { LookupService, type State, type City } from '@/services/modules/LookupService'
import { SalesLinkService, type DiscountCouponPayload } from '@/services/modules/SalesLinkService'
import { DegreeRelationshipsService, type DegreeRelationship } from '@/services/modules/DegreeRelationshipsService'
import { MaritalStatusesService, type MaritalStatus } from '@/services/modules/MaritalStatusesService'

const maskCepLive = (v: string) => onlyDigits(v).slice(0, 8).replace(/^(\d{5})(\d)/, '$1-$2')
const maskCpfLive = (v: string) => onlyDigits(v).slice(0, 11)
  .replace(/^(\d{3})(\d)/, '$1.$2').replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3').replace(/\.(\d{3})(\d)/, '.$1-$2')
const GENDERS = ['MASCULINO', 'FEMININO', 'OUTRO']

function ageFrom(birth: string | null): number | null {
  // Parse dos componentes da string YYYY-MM-DD (sem `new Date(birth)`) para
  // evitar o shift de fuso que altera o dia em BRT (−3).
  const parts = /^(\d{4})-(\d{2})-(\d{2})/.exec((birth ?? '').trim())
  if (!parts) return null
  const by = +parts[1], bm = +parts[2], bd = +parts[3]
  const now = new Date()
  let a = now.getFullYear() - by
  const m = (now.getMonth() + 1) - bm
  if (m < 0 || (m === 0 && now.getDate() < bd)) a--
  return a
}

interface FormState {
  name: string; socialName: string; cpf: string; rg: string; birthDate: string
  maritalStatusId: string; gender: string; email: string; phone: string; mobile: string
  companyBranchId: string; companyDepartmentId: string; status: 'ATIVO' | 'INATIVO'
  postalCode: string; address: string; number: string; complement: string; district: string; stateId: string; cityId: string
  planId: string
}
const EMPTY: FormState = {
  name: '', socialName: '', cpf: '', rg: '', birthDate: '',
  maritalStatusId: '', gender: '', email: '', phone: '', mobile: '',
  companyBranchId: '', companyDepartmentId: '', status: 'ATIVO',
  postalCode: '', address: '', number: '', complement: '', district: '', stateId: '', cityId: '',
  planId: '',
}

export function CollaboratorFormPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id?: string }>()
  const editing = !!id

  const [activeSection, setActiveSection] = useState('dados')
  const [f, setF] = useState<FormState>({ ...EMPTY })
  const patch = (p: Partial<FormState>) => setF(prev => ({ ...prev, ...p }))
  const [deps, setDeps] = useState<CollaboratorDependent[]>([])
  const [errors, setErrors] = useState<Record<string, string>>({})
  const clearError = (k: string) => setErrors(prev => (prev[k] ? { ...prev, [k]: '' } : prev))

  const [plans, setPlans] = useState<ReleasedPlan[]>([])
  const [branches, setBranches] = useState<SimpleOption[]>([])
  const [departments, setDepartments] = useState<DepartmentOption[]>([])
  const [degrees, setDegrees] = useState<DegreeRelationship[]>([])
  const [maritals, setMaritals] = useState<MaritalStatus[]>([])
  const [states, setStates] = useState<State[]>([])
  const [cities, setCities] = useState<City[]>([])

  // Cupom (mesmo fluxo do admin: validar por código + plano, mostrar desconto).
  const [coupon, setCoupon] = useState<DiscountCouponPayload | null>(null)
  const [couponCode, setCouponCode] = useState('')
  const [couponLoading, setCouponLoading] = useState(false)

  const [loading, setLoading] = useState(editing)
  const [saving, setSaving] = useState(false)
  const [cepLoading, setCepLoading] = useState(false)
  const [depEditor, setDepEditor] = useState<{ open: boolean; index: number | null }>({ open: false, index: null })

  useEffect(() => {
    CollaboratorsService.releasedPlans().then(setPlans).catch(() => {})
    MyCompanyService.branches().then(setBranches).catch(() => {})
    MyCompanyService.departments().then(setDepartments).catch(() => {})
    DegreeRelationshipsService.list({ size: 0, status: 'ATIVO' }).then(r => setDegrees(r.data)).catch(() => {})
    MaritalStatusesService.list({ size: 0, status: 'ATIVO' }).then(r => setMaritals(r.data)).catch(() => {})
    LookupService.states().then(setStates).catch(() => {})
  }, [])

  useEffect(() => {
    if (!f.stateId) { setCities([]); return }
    LookupService.cities(f.stateId).then(setCities).catch(() => setCities([]))
  }, [f.stateId])

  useEffect(() => {
    if (!editing) return
    setLoading(true)
    CollaboratorsService.show(id!).then(c => {
      setF({
        name: c.name ?? '', socialName: c.socialName ?? '', cpf: c.cpf ? maskCpfLive(c.cpf) : '', rg: c.rg ?? '',
        birthDate: c.birthDate ?? '', maritalStatusId: c.maritalStatusId ?? '', gender: c.gender ?? '',
        email: c.email ?? '', phone: c.phone ? maskPhoneInput(c.phone) : '', mobile: c.mobile ? maskPhoneInput(c.mobile) : '',
        companyBranchId: c.companyBranchId ?? '', companyDepartmentId: c.companyDepartmentId ?? '', status: c.status ?? 'ATIVO',
        postalCode: c.address?.postalCode ? maskCepLive(c.address.postalCode) : '', address: c.address?.address ?? '',
        number: c.address?.number ?? '', complement: c.address?.complement ?? '', district: c.address?.district ?? '',
        stateId: c.address?.city?.state?.id ?? '', cityId: c.address?.city?.id ?? '',
        planId: c.planId ?? '',
      })
      setDeps((c.dependents ?? []).map(d => ({ ...d, cpf: d.cpf ? maskCpfLive(d.cpf) : null })))
      // Pré-carrega o cupom aplicado direto da relation (mostra mesmo que o
      // cupom já esteja expirado/indisponível — reflete o que está gravado).
      if (c.discountCoupon) {
        setCoupon({
          id: c.discountCoupon.id,
          code: c.discountCoupon.code,
          discountType: c.discountCoupon.discountType,
          value: c.discountCoupon.value,
          monthlyInstallments: c.discountCoupon.monthlyInstallments,
        })
        setCouponCode(c.discountCoupon.code)
      }
    }).catch(() => toast.error('Erro ao carregar o colaborador.')).finally(() => setLoading(false))
  }, [id, editing])

  const selectedPlan = useMemo(() => plans.find(p => p.id === f.planId) ?? null, [plans, f.planId])
  const allowedDegrees = useMemo(() => selectedPlan?.eligibilities ?? [], [selectedPlan])

  async function applyCoupon() {
    if (!f.planId) { toast.error({ title: 'Selecione o plano', description: 'O cupom é validado para o plano escolhido.' }); return }
    if (!couponCode.trim()) return
    setCouponLoading(true)
    try {
      const c = await CollaboratorsService.findCoupon(couponCode.trim(), f.planId)
      if (!c) { toast.error({ title: 'Cupom inválido', description: 'Cupom inexistente, expirado ou não aplicável a este plano.' }); return }
      setCoupon(c)
      toast.success({ title: 'Cupom aplicado', description: c.code })
    } catch {
      toast.error({ title: 'Cupom inválido', description: 'Cupom inexistente, expirado ou não aplicável a este plano.' })
    } finally { setCouponLoading(false) }
  }
  function removeCoupon() { setCoupon(null); setCouponCode('') }

  /** Detalhe do desconto do cupom sobre o plano selecionado (igual ao admin). */
  function couponBreakdownFor(c: DiscountCouponPayload | null) {
    if (!c) return null
    const original = selectedPlan?.price ?? 0
    const discount = c.discountType === 'PORCENTAGEM'
      ? Math.min(original * (c.value / 100), original)
      : Math.min(c.value, original)
    const final = Math.max(0, original - discount)
    const discountLabel = c.discountType === 'PORCENTAGEM'
      ? `${c.value.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}% (${brl(discount)})`
      : brl(c.value)
    return { original, discount, final, discountLabel }
  }

  // Trocar de plano invalida o cupom aplicado (a validade é por plano).
  const prevPlanRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    const prev = prevPlanRef.current
    prevPlanRef.current = f.planId
    if (prev && prev !== f.planId && coupon) {
      setCoupon(null)
      setCouponCode('')
      toast.warning({ title: 'Cupom removido', description: 'Você alterou o plano. Aplique o cupom novamente para validá-lo.' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.planId])

  // Departamentos exibidos: ao escolher uma filial, só os dela; sem filial
  // (matriz), todos. (Departamento pode ser compartilhado entre filiais.)
  const filteredDepartments = useMemo(
    () => f.companyBranchId ? departments.filter(d => d.branchIds.includes(f.companyBranchId)) : departments,
    [departments, f.companyBranchId],
  )

  async function lookupCep() {
    const cep = onlyDigits(f.postalCode)
    if (cep.length !== 8) { toast.error({ title: 'CEP inválido', description: 'O CEP precisa ter 8 dígitos.' }); return }
    setCepLoading(true)
    try {
      const r = await SalesLinkService.lookupCep(cep)
      if (!r) { toast.error('CEP não encontrado.'); return }
      const st = states.find(s => s.abbreviation === r.stateAbbr)
      let cityId = ''
      if (st) {
        let cs = await LookupService.cities(st.id)
        let ci = cs.find(c => c.city.toLowerCase() === r.cityName.toLowerCase())
        if (!ci) { try { ci = await LookupService.findOrCreateCity(r.cityName, st.abbreviation); cs = [...cs, ci] } catch { /* ignore */ } }
        setCities(cs); cityId = ci?.id ?? ''
      }
      patch({ address: r.address ?? '', district: r.district ?? '', stateId: st?.id ?? '', cityId })
    } catch { toast.error('Não foi possível buscar o CEP.') }
    finally { setCepLoading(false) }
  }

  function validate(): boolean {
    const e: Record<string, string> = {}
    if (!f.name.trim()) e.name = 'Informe o nome.'
    if (!f.cpf || !f.cpf.trim()) e.cpf = 'Informe o CPF.'
    else if (onlyDigits(f.cpf).length !== 11) e.cpf = 'CPF deve ter 11 dígitos.'
    if (!f.birthDate || !f.birthDate.trim()) e.birthDate = 'Informe a data de nascimento.'
    if (!f.planId) e.planId = 'Selecione o plano.'
    setErrors(e)
    if (e.name || e.cpf || e.birthDate) setActiveSection('dados')
    else if (e.planId) setActiveSection('plano')
    return Object.keys(e).length === 0
  }

  async function submit() {
    if (!validate()) { toast.error({ title: 'Verifique os campos', description: 'Há campos obrigatórios.' }); return }
    setSaving(true)
    try {
      const payload: Record<string, unknown> = {
        name: f.name.trim(), social_name: f.socialName || null,
        cpf: f.cpf ? onlyDigits(f.cpf) : null, rg: f.rg || null, birth_date: f.birthDate || null,
        plan_id: f.planId,
        marital_status_id: f.maritalStatusId || null, gender: f.gender || null,
        email: f.email || null, phone: f.phone ? onlyDigits(f.phone) : null, mobile: f.mobile ? onlyDigits(f.mobile) : null,
        company_branch_id: f.companyBranchId || null, company_department_id: f.companyDepartmentId || null,
        discount_coupon_id: coupon?.id ?? null, coupon: coupon?.code ?? null, status: f.status,
        postal_code: f.postalCode ? onlyDigits(f.postalCode) : null, address: f.address || null,
        number: f.number || null, complement: f.complement || null, district: f.district || null, city_id: f.cityId || null,
        dependents: deps.map(d => ({
          id: d.id, name: d.name, cpf: d.cpf ? onlyDigits(d.cpf) : null, birth_date: d.birthDate || null,
          degree_relationship_id: d.degreeRelationshipId || null, gender: d.gender || null, status: d.status,
        })),
      }
      if (editing) { await CollaboratorsService.update(id!, payload); toast.success({ title: 'Colaborador atualizado', description: f.name }) }
      else { await CollaboratorsService.create(payload); toast.success({ title: 'Colaborador cadastrado', description: f.name }) }
      navigate('/my-company/collaborators')
    } catch (err) {
      toast.error((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Erro ao salvar.')
    } finally { setSaving(false) }
  }

  const dadosErr = !!(errors.name || errors.cpf)
  const planoErr = !!errors.planId

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 size={28} className="animate-spin text-gray-400" /></div>
  }

  const sections: FormSection[] = [
    {
      key: 'dados', label: 'Dados', icon: UserIcon, hasError: dadosErr, errorCount: dadosErr ? 1 : 0,
      content: (
        <SectionShell title="Dados" description="Informações do colaborador (titular).">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2"><Input label="Nome completo" required value={f.name} onChange={e => { patch({ name: e.target.value }); clearError('name') }} placeholder="Nome do colaborador" error={errors.name} /></div>
            <Input label="Nome social" value={f.socialName} onChange={e => patch({ socialName: e.target.value })} placeholder="Opcional" />
            <Input label="CPF" required value={f.cpf} onChange={e => { patch({ cpf: maskCpfLive(e.target.value) }); clearError('cpf') }} placeholder="000.000.000-00" error={errors.cpf} />
            <Input label="RG" value={f.rg} onChange={e => patch({ rg: e.target.value })} placeholder="Opcional" />
            <Input label="Data de nascimento" type="date" required value={f.birthDate} onChange={e => { patch({ birthDate: e.target.value }); clearError('birthDate') }} clearable={false} error={errors.birthDate} />
            <Select label="Estado civil" value={f.maritalStatusId} onChange={e => patch({ maritalStatusId: e.target.value })} placeholder="Selecione" options={maritals.map(m => ({ value: m.id, label: m.maritalStatus }))} />
            <Select label="Gênero" value={f.gender} onChange={e => patch({ gender: e.target.value })} placeholder="Selecione" options={GENDERS.map(g => ({ value: g, label: g }))} />
            <Input label="E-mail" type="email" value={f.email} onChange={e => patch({ email: e.target.value })} placeholder="nome@empresa.com.br" />
            <Input label="Telefone" value={f.phone} onChange={e => patch({ phone: maskPhoneInput(e.target.value) })} placeholder="(11) 3333-4444" />
            <Input label="Celular / WhatsApp" value={f.mobile} onChange={e => patch({ mobile: maskPhoneInput(e.target.value) })} placeholder="(11) 99999-9999" />
            <Select label="Filial" value={f.companyBranchId} onChange={e => patch({ companyBranchId: e.target.value, companyDepartmentId: '' })} placeholder="Matriz (sem filial)" options={branches.map(b => ({ value: b.id, label: b.name }))} />
            <Select label="Departamento" value={f.companyDepartmentId} onChange={e => patch({ companyDepartmentId: e.target.value })} placeholder={filteredDepartments.length ? 'Sem departamento' : 'Nenhum departamento'} options={filteredDepartments.map(d => ({ value: d.id, label: d.name }))} />
            <div className="md:col-span-2 border-t border-gray-100 pt-4"><StatusToggle value={f.status} onChange={v => patch({ status: v })} /></div>
          </div>
        </SectionShell>
      ),
    },
    {
      key: 'local', label: 'Localização', icon: MapPin,
      content: (
        <SectionShell title="Localização" description="Digite o CEP e clique na lupa para preencher automaticamente.">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input label="CEP" value={f.postalCode} onChange={e => patch({ postalCode: maskCepLive(e.target.value) })} placeholder="00000-000"
              actionRight={<button type="button" onClick={lookupCep} disabled={cepLoading} aria-label="Buscar CEP" className="cursor-pointer inline-flex items-center justify-center w-7 h-7 rounded-md text-gray-500 hover:text-brand-600 hover:bg-gray-100 disabled:opacity-50">{cepLoading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}</button>} />
            <div className="md:col-span-2"><Input label="Logradouro" value={f.address} onChange={e => patch({ address: e.target.value })} placeholder="Rua, avenida" /></div>
            <Input label="Número" value={f.number} onChange={e => patch({ number: e.target.value })} placeholder="123" />
            <Input label="Complemento" value={f.complement} onChange={e => patch({ complement: e.target.value })} placeholder="Sala, bloco" />
            <Input label="Bairro" value={f.district} onChange={e => patch({ district: e.target.value })} placeholder="Bairro" />
            <Select label="Estado" value={f.stateId} onChange={e => patch({ stateId: e.target.value, cityId: '' })} placeholder="UF" options={states.map(s => ({ value: s.id, label: s.abbreviation }))} />
            <div className="md:col-span-2"><Select label="Cidade" value={f.cityId} onChange={e => patch({ cityId: e.target.value })} placeholder={f.stateId ? 'Selecione' : 'Escolha o estado'} disabled={!f.stateId} options={cities.map(c => ({ value: c.id, label: c.city }))} /></div>
          </div>
        </SectionShell>
      ),
    },
    {
      key: 'plano', label: 'Plano', icon: Package, hasError: planoErr, errorCount: planoErr ? 1 : 0,
      content: (
        <SectionShell title="Plano" description="Apenas planos liberados para a sua empresa.">
          <div className="max-w-md">
            <Select label="Plano" required value={f.planId} onChange={e => patch({ planId: e.target.value })}
              placeholder={plans.length ? 'Selecione o plano' : 'Nenhum plano liberado para a empresa'}
              options={plans.map(p => ({ value: p.id, label: p.planType ? `${p.name} · ${p.planType}` : p.name }))}
              error={errors.planId} />
            {selectedPlan && (
              <p className="text-[11px] text-gray-500 mt-2">
                Graus de parentesco permitidos para dependentes: {allowedDegrees.length ? allowedDegrees.map(d => d.degreeRelationship).join(', ') : 'nenhum (apenas titular)'}.
              </p>
            )}
          </div>
        </SectionShell>
      ),
    },
    {
      key: 'deps', label: `Dependentes (${deps.length})`, icon: UsersIcon,
      content: (
        <SectionShell title="Dependentes" description="O grau de parentesco depende do pacote do plano. As alterações só são gravadas ao clicar em Salvar (botão do topo).">
          <DependentsTab deps={deps} degrees={degrees} allowedDegrees={allowedDegrees} planSelected={!!f.planId}
            onAdd={() => setDepEditor({ open: true, index: null })}
            onEdit={i => setDepEditor({ open: true, index: i })}
            onRemove={i => setDeps(prev => prev.filter((_, idx) => idx !== i))} />
        </SectionShell>
      ),
    },
    {
      key: 'cupom', label: 'Cupom', icon: Tag,
      content: (
        <SectionShell title="Cupom de desconto" description="Aplique um código de cupom válido para o plano selecionado.">
          {coupon ? (
            (() => {
              const b = couponBreakdownFor(coupon)
              return (
                <div className="flex items-start justify-between gap-3 rounded-md ring-1 ring-emerald-200 bg-emerald-50 px-4 py-3">
                  <div className="flex items-start gap-2 text-sm">
                    <Check size={16} className="text-emerald-600 mt-0.5 shrink-0" />
                    <div>
                      <div className="font-semibold text-emerald-800">{coupon.code}</div>
                      {b && (
                        <dl className="mt-1 space-y-0.5 text-xs text-emerald-700">
                          <div className="flex gap-2"><dt className="text-emerald-600">Valor do plano:</dt><dd className="font-medium">{brl(b.original)}</dd></div>
                          <div className="flex gap-2"><dt className="text-emerald-600">Desconto:</dt><dd className="font-medium">{b.discountLabel}</dd></div>
                          <div className="flex gap-2"><dt className="text-emerald-600">Valor final:</dt><dd className="font-semibold">{brl(b.final)}</dd></div>
                        </dl>
                      )}
                    </div>
                  </div>
                  <button type="button" onClick={removeCoupon} className="cursor-pointer inline-flex items-center gap-1 text-xs text-rose-600 hover:underline shrink-0">
                    <X size={12} /> Remover
                  </button>
                </div>
              )
            })()
          ) : (
            <div className="flex items-end gap-3">
              <Input label="Código do cupom" value={couponCode} onChange={(e) => setCouponCode(e.target.value)} placeholder="amplamaisparceiros" wrapperClassName="max-w-xs" clearable={false} />
              <Button type="button" onClick={applyCoupon} loading={couponLoading} iconLeft={<Ticket size={14} />} disabled={!f.planId || !couponCode.trim()}>
                Aplicar
              </Button>
            </div>
          )}
          {!f.planId && <p className="mt-2 text-xs text-gray-400">Selecione um plano antes de aplicar o cupom.</p>}
        </SectionShell>
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title={editing ? 'Editar colaborador' : 'Novo colaborador'}
        subtitle="Vinculado automaticamente à sua empresa"
        crumbs={[{ label: 'Minha empresa' }, { label: 'Colaboradores', to: '/my-company/collaborators' }, { label: editing ? 'Editar' : 'Novo' }]}
        backTo="/my-company/collaborators"
        actions={<Button onClick={submit} loading={saving} iconLeft={<Save size={14} />}>Salvar</Button>}
      />
      <FormSidemenu sections={sections} activeKey={activeSection} onChange={setActiveSection} />

      {depEditor.open && (
        <DependentEditor
          dependent={depEditor.index !== null ? deps[depEditor.index] : null}
          degrees={degrees} allowedDegrees={allowedDegrees}
          onClose={() => setDepEditor({ open: false, index: null })}
          onSave={(dep) => {
            setDeps(prev => depEditor.index !== null ? prev.map((d, i) => i === depEditor.index ? dep : d) : [...prev, dep])
            setDepEditor({ open: false, index: null })
          }}
        />
      )}
    </>
  )
}

function SectionShell({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-md ring-1 ring-gray-200 p-5 sm:p-6">
      <div className="mb-4">
        <h2 className="text-sm font-semibold text-gray-800">{title}</h2>
        {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
      </div>
      {children}
    </div>
  )
}

function DependentsTab({ deps, degrees, allowedDegrees, planSelected, onAdd, onEdit, onRemove }: {
  deps: CollaboratorDependent[]; degrees: DegreeRelationship[]; allowedDegrees: PlanEligibility[]
  planSelected: boolean; onAdd: () => void; onEdit: (i: number) => void; onRemove: (i: number) => void
}) {
  const degreeName = (id: string | null) => degrees.find(d => d.id === id)?.degreeRelationship ?? '—'
  if (!planSelected) return <p className="text-sm text-gray-500">Selecione o plano na aba <strong>Plano</strong> para liberar os dependentes.</p>
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs text-gray-500">{allowedDegrees.length ? 'Adicione dependentes conforme os graus permitidos pelo pacote.' : 'Este pacote não permite dependentes.'}</span>
        <Button size="sm" variant="ghost" iconLeft={<Plus size={12} />} onClick={onAdd} disabled={!allowedDegrees.length}>Adicionar dependente</Button>
      </div>
      {deps.length === 0 ? (
        <div className="rounded-md border border-dashed border-gray-300 p-6 text-center text-sm text-gray-400">Nenhum dependente.</div>
      ) : (
        <ul className="space-y-2">
          {deps.map((d, i) => (
            <li key={i} className="flex items-center justify-between gap-3 rounded-md ring-1 ring-gray-200 bg-gray-50 px-3 py-2.5">
              <div className="min-w-0">
                <div className="text-sm font-medium text-gray-800 break-words">{d.name}</div>
                <div className="text-[11px] text-gray-500">
                  {degreeName(d.degreeRelationshipId)}{d.cpf ? ` · ${d.cpf}` : ''}
                  {(d.formatted?.membershipCardNumber || d.membershipCardNumber) ? ` · Carteirinha: ${d.formatted?.membershipCardNumber || d.membershipCardNumber}` : ''}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button type="button" onClick={() => onEdit(i)} className="cursor-pointer p-1.5 rounded text-gray-500 hover:bg-gray-100"><Pencil size={14} /></button>
                <button type="button" onClick={() => onRemove(i)} className="cursor-pointer p-1.5 rounded text-rose-500 hover:bg-rose-50"><Trash2 size={14} /></button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// Dependente: add/editar via SHEET lateral (regra global deste módulo).
function DependentEditor({ dependent, degrees, allowedDegrees, onClose, onSave }: {
  dependent: CollaboratorDependent | null; degrees: DegreeRelationship[]; allowedDegrees: PlanEligibility[]
  onClose: () => void; onSave: (d: CollaboratorDependent) => void
}) {
  const [name, setName] = useState(dependent?.name ?? '')
  const [cpf, setCpf] = useState(dependent?.cpf ?? '')
  const [birthDate, setBirthDate] = useState(dependent?.birthDate ?? '')
  const [degreeId, setDegreeId] = useState(dependent?.degreeRelationshipId ?? '')
  const [gender, setGender] = useState(dependent?.gender ?? '')
  const [errors, setErrors] = useState<Record<string, string>>({})

  const options = allowedDegrees.map(a => ({ value: a.degreeRelationshipId, label: degrees.find(d => d.id === a.degreeRelationshipId)?.degreeRelationship ?? a.degreeRelationship ?? '—' }))
  const minAge = allowedDegrees.find(a => a.degreeRelationshipId === degreeId)?.minimumAge ?? null

  function save() {
    const e: Record<string, string> = {}
    if (!name.trim()) e.name = 'Informe o nome.'
    if (!degreeId) e.degree = 'Selecione o grau de parentesco.'
    if (!cpf || !cpf.trim()) e.cpf = 'Informe o CPF.'
    else if (onlyDigits(cpf).length !== 11) e.cpf = 'CPF deve ter 11 dígitos.'
    if (!birthDate || !birthDate.trim()) {
      e.birthDate = 'Informe a data de nascimento.'
    } else if (minAge != null) {
      const a = ageFrom(birthDate)
      if (a != null && a < minAge) e.birthDate = `Idade mínima para este grau: ${minAge} anos.`
    }
    setErrors(e)
    if (Object.keys(e).length) return
    onSave({ id: dependent?.id, name: name.trim(), cpf: cpf || null, birthDate: birthDate || null, degreeRelationshipId: degreeId, gender: gender || null, status: dependent?.status ?? 'ATIVO' })
  }

  return (
    <Sheet open onClose={onClose} size="lg" title={dependent ? 'Editar dependente' : 'Novo dependente'}
      footer={<><Button variant="ghost" onClick={onClose}>Cancelar</Button><Button onClick={save} iconLeft={<Check size={14} />}>Confirmar</Button></>}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2"><Input label="Nome completo" required value={name} onChange={e => { setName(e.target.value); setErrors(p => ({ ...p, name: '' })) }} placeholder="Nome do dependente" error={errors.name} /></div>
        <Select label="Grau de parentesco" required value={degreeId} onChange={e => { setDegreeId(e.target.value); setErrors(p => ({ ...p, degree: '' })) }} placeholder="Selecione" options={options} error={errors.degree} hint={minAge != null ? `Idade mínima: ${minAge} anos.` : undefined} />
        <Input label="CPF" required value={cpf} onChange={e => { setCpf(maskCpfLive(e.target.value)); setErrors(p => ({ ...p, cpf: '' })) }} placeholder="000.000.000-00" error={errors.cpf} />
        <Input label="Data de nascimento" type="date" required value={birthDate} onChange={e => { setBirthDate(e.target.value); setErrors(p => ({ ...p, birthDate: '' })) }} clearable={false} error={errors.birthDate} />
        <Select label="Gênero" value={gender} onChange={e => setGender(e.target.value)} placeholder="Selecione" options={GENDERS.map(g => ({ value: g, label: g }))} />
      </div>
    </Sheet>
  )
}
